import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import toast from 'react-hot-toast';
import api from '../lib/api';

const { FiFileText, FiCheckCircle, FiSend, FiDownload, FiEdit3, FiUsers, FiShield, FiLoader } = FiIcons;

const CaregiverReportGenerator = ({ patient }) => {
  const [step, setStep] = useState('review'); // review, verify, transmit
  const [isProcessing, setIsProcessing] = useState(false);
  const [notes, setNotes] = useState('');
  
  // Mock AI-generated summary data
  const [summary] = useState({
    period: "Last 24 Hours",
    stabilityScore: 88,
    incidents: 0,
    moodTrend: "Stable/Improving",
    observations: [
      "Patient maintained consistent sleep cycle (7.5h).",
      "No threshold breaches detected in heart rate.",
      "Mood indicators show 15% reduction in anxiety markers."
    ]
  });

  const handleVerify = () => {
    setStep('verify');
    toast.success("Summary verified. Ready for transmission.");
  };

  const handleTransmit = async (recipient) => {
    setIsProcessing(true);
    try {
      // Simulate API call to transmit report
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success(`Report transmitted to ${recipient}`);
      setStep('complete');
    } catch (err) {
      toast.error("Transmission failed");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-white rounded-[3rem] border border-gray-100 shadow-2xl overflow-hidden">
      <div className="p-10 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
        <div>
          <h3 className="text-2xl font-black text-gray-900 tracking-tight">Shift Reporting</h3>
          <p className="text-sm text-gray-400 font-bold uppercase tracking-widest mt-1">Automated Patient Synthesis</p>
        </div>
        <div className="flex bg-white p-1 rounded-xl shadow-sm border border-gray-100">
          {['review', 'verify', 'transmit'].map((s, i) => (
            <div key={s} className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest ${step === s ? 'text-indigo-600' : 'text-gray-300'}`}>
              0{i + 1} {s}
            </div>
          ))}
        </div>
      </div>

      <div className="p-10">
        <AnimatePresence mode="wait">
          {step === 'review' && (
            <motion.div key="review" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 bg-indigo-50 rounded-3xl border border-indigo-100">
                  <p className="text-[10px] font-black text-indigo-400 uppercase mb-2">Patient Stability</p>
                  <div className="text-4xl font-black text-indigo-900">{summary.stabilityScore}%</div>
                </div>
                <div className="p-6 bg-emerald-50 rounded-3xl border border-emerald-100">
                  <p className="text-[10px] font-black text-emerald-400 uppercase mb-2">Incident Count</p>
                  <div className="text-4xl font-black text-emerald-900">{summary.incidents}</div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest">AI Observations</h4>
                {summary.observations.map((obs, i) => (
                  <div key={i} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-2xl">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" />
                    <p className="text-sm font-medium text-gray-700">{obs}</p>
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest">Caregiver Notes (Optional)</h4>
                <textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full p-6 bg-gray-50 border-none rounded-3xl text-sm min-h-[120px] focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="Add any qualitative observations from your visit..."
                />
              </div>

              <button 
                onClick={handleVerify}
                className="w-full py-5 bg-gray-900 text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl active:scale-95 transition-all flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiEdit3} />
                <span>Verify & Lock Report</span>
              </button>
            </motion.div>
          )}

          {step === 'verify' && (
            <motion.div key="verify" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-10">
              <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiShield} className="text-3xl" />
              </div>
              <h4 className="text-2xl font-black text-gray-900 mb-2">Report Authenticated</h4>
              <p className="text-gray-500 mb-10 max-w-sm mx-auto">This report has been cryptographically hashed and is ready for institutional transmission.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button 
                  onClick={() => setStep('transmit')}
                  className="py-5 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl active:scale-95 transition-all flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiSend} />
                  <span>Transmit to Team</span>
                </button>
                <button className="py-5 bg-white text-gray-900 border-2 border-gray-100 rounded-2xl font-black uppercase tracking-widest text-xs shadow-sm hover:bg-gray-50 transition-all flex items-center justify-center space-x-2">
                  <SafeIcon icon={FiDownload} />
                  <span>Download PDF</span>
                </button>
              </div>
            </motion.div>
          )}

          {step === 'transmit' && (
            <motion.div key="transmit" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <h4 className="text-sm font-black text-gray-900 uppercase tracking-widest mb-6">Select Distribution List</h4>
              {[
                { name: "Unit Manager", role: "Management", email: "manager@institution.com" },
                { name: "Program Director", role: "Leadership", email: "director@institution.com" },
                { name: "On-Call Nurse", role: "Clinical", email: "clinical-lead@institution.com" }
              ].map((rec, i) => (
                <button 
                  key={i}
                  disabled={isProcessing}
                  onClick={() => handleTransmit(rec.name)}
                  className="w-full p-6 bg-gray-50 border border-gray-100 rounded-[2rem] flex items-center justify-between group hover:bg-white hover:shadow-xl transition-all disabled:opacity-50"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm">
                      <SafeIcon icon={FiUsers} className="text-indigo-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-black text-gray-900">{rec.name}</p>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{rec.role}</p>
                    </div>
                  </div>
                  {isProcessing ? <SafeIcon icon={FiLoader} className="animate-spin text-gray-400" /> : <SafeIcon icon={FiSend} className="text-gray-400 group-hover:text-indigo-600" />}
                </button>
              ))}
              <button 
                onClick={() => setStep('verify')}
                className="w-full py-4 text-gray-400 font-bold text-xs uppercase tracking-widest"
              >
                Back to Document
              </button>
            </motion.div>
          )}

          {step === 'complete' && (
            <motion.div key="complete" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-20">
              <div className="w-24 h-24 bg-emerald-500 text-white rounded-[2rem] flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-emerald-200">
                <SafeIcon icon={FiCheckCircle} className="text-4xl" />
              </div>
              <h4 className="text-3xl font-black text-gray-900 mb-2">Cycle Complete</h4>
              <p className="text-gray-500 mb-8">The shift summary has been delivered and archived.</p>
              <button 
                onClick={() => { setStep('review'); setNotes(''); }}
                className="px-10 py-4 bg-gray-900 text-white rounded-2xl font-black uppercase tracking-widest text-xs"
              >
                Start New Report
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CaregiverReportGenerator;